/**
 * Vital Leopard early access — two-step gate backed by the REAL Ancarta Brand Rep backend.
 *
 * BUSINESS RULE: every customer with Leopard early access is an Ancarta Brand Rep.
 *   Step 1  email  ->  POST {endpoint} {step:'check'}   server answers ONE bit: access | needs details
 *   Step 2  (only when told) name + Instagram + Brand Rep code + Terms/Privacy/emails consent
 *           ->  POST {endpoint} {step:'join'}           server runs the existing production Brand Rep signup, then answers access
 *
 * PRIVACY: this script never receives — and the server never sends — any customer data (name, Instagram, code, private code,
 * tags, id, consent, orders). The only thing it learns is "unlock" or "ask for details". It never unlocks before the server
 * says yes. There is no lookup endpoint and no Shopify customer form on this page (so this page creates no tags/consent
 * events of its own; new Brand Reps are created exactly as on /pages/brand-rep, with source attribution "leopard").
 *
 * SOURCE: `?src=` is matched against a fixed allow-list; anything else becomes `direct`. Only that allow-listed value is sent
 * (as `lane`) and used in analytics. It never contains PII. The server re-validates it against the same allow-list.
 *
 * STORAGE: exactly one non-sensitive flag, `ancartaLeopardAccess = '1'`. Email, name, Instagram and code live only in the
 * form fields on screen — never localStorage/sessionStorage/cookies, never analytics, never a URL.
 *
 * ANALYTICS: Shopify's custom-event publisher only (`leopard_access_submitted`, `leopard_access_unlocked`), payload
 * { source, step } / { source, via }. A no-op unless a Customer Events pixel subscribes. Standard commerce events untouched.
 *
 * Idempotency: one UUID per attempt (kept on a network-error retry, regenerated after every definitive response — the same
 * discipline as assets/brand-rep.js), so a double-click can never create two Brand Reps and a stale cached answer is never replayed.
 */

const ACCESS_KEY = 'ancartaLeopardAccess';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Campaign lanes the page may report. Everything else -> 'direct'. */
const ALLOWED_SOURCES = ['ruby_dm_previous_buyer', 'ruby_dm_private_signup', 'ruby_story', 'ancarta_instagram', 'ancarta_email'];

function publish(name, payload) {
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    /* tracking must never affect access */
  }
}

function remember() {
  try {
    window.localStorage.setItem(ACCESS_KEY, '1');
  } catch (error) {
    /* storage unavailable (private mode / in-app browser) — she simply sees the gate next visit */
  }
}

function forget() {
  try {
    window.localStorage.removeItem(ACCESS_KEY);
  } catch (error) {
    /* nothing to clear */
  }
}

function currentSource() {
  try {
    const raw = (new URLSearchParams(window.location.search).get('src') || '').trim().toLowerCase();
    return ALLOWED_SOURCES.includes(raw) ? raw : 'direct';
  } catch (error) {
    return 'direct';
  }
}

function newKey() {
  try {
    return window.crypto.randomUUID();
  } catch (error) {
    return `k-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function init() {
  const root = document.querySelector('[data-leopard-drop]');
  if (!root) return;

  const form = root.querySelector('#leopard-access-form');
  const status = root.querySelector('[data-leopard-status]');
  const unlockedPanel = root.querySelector('[data-leopard-unlocked]');
  const isEditor = Boolean(window.Shopify?.designMode);
  const source = currentSource();

  const unlockUi = (via) => {
    remember();
    root.classList.add('leopard-drop--revealing');
    root.setAttribute('data-access', 'unlocked');
    if (status) status.textContent = 'Vital Leopard unlocked.';
    unlockedPanel?.focus({ preventScroll: true });
    if (!isEditor) publish('leopard_access_unlocked', { source, via });
  };

  // Theme-editor helpers only (never rendered for customers): preview each state without submitting anything.
  root.querySelector('[data-leopard-reset]')?.addEventListener('click', () => {
    forget();
    root.classList.remove('leopard-drop--revealing');
    root.setAttribute('data-access', 'gate');
    if (form) showStep('email');
  });
  root.querySelector('[data-leopard-editor-unlock]')?.addEventListener('click', () => {
    root.classList.add('leopard-drop--revealing');
    root.setAttribute('data-access', 'unlocked');
  });
  root.querySelector('[data-leopard-editor-step2]')?.addEventListener('click', () => showStep('details'));

  // Already unlocked by the remembered flag (inline script before first paint).
  if (root.getAttribute('data-access') === 'unlocked') {
    remember();
    if (!isEditor) publish('leopard_access_unlocked', { source, via: 'remembered' });
  }

  if (!form) return;

  const endpoint = (form.dataset.endpoint || '').trim();
  const emailInput = form.querySelector('#LeopardAccessEmail');
  const nameInput = form.querySelector('#LeopardAccessName');
  const instagramInput = form.querySelector('#LeopardAccessInstagram');
  const codeInput = form.querySelector('#LeopardAccessCode');
  const consentInput = form.querySelector('#LeopardAccessConsent');
  const honeypot = form.querySelector('[data-leopard-hp]');
  const errorEl = form.querySelector('[data-leopard-error]');
  const suggestionsEl = form.querySelector('[data-leopard-suggestions]');
  const submitButton = form.querySelector('[data-leopard-submit]');
  const backButton = form.querySelector('[data-leopard-back]');
  const stepEmail = form.querySelector('[data-leopard-step="email"]');
  const stepDetails = form.querySelector('[data-leopard-step="details"]');

  let step = 'email';
  let busy = false;
  let idempotencyKey = newKey();

  function showStep(next) {
    step = next;
    if (stepEmail) stepEmail.hidden = next !== 'email';
    if (stepDetails) stepDetails.hidden = next !== 'details';
    if (backButton) backButton.hidden = next !== 'details';
    if (submitButton) submitButton.textContent = (next === 'details' ? submitButton.dataset.labelDetails : submitButton.dataset.labelEmail) || submitButton.textContent;
    clearError();
    if (next === 'details') nameInput?.focus({ preventScroll: false });
  }

  function showError(message, field) {
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', 'LeopardAccessError');
      field.focus?.({ preventScroll: false });
    }
  }

  function clearError() {
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
    if (suggestionsEl) {
      suggestionsEl.hidden = true;
      suggestionsEl.textContent = '';
    }
    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
  }

  function setBusy(value) {
    busy = value;
    if (submitButton) submitButton.disabled = value;
  }

  // The code box only ever holds A–Z / 0–9 (the same rule the backend enforces), shown upper-case.
  codeInput?.addEventListener('input', () => {
    const cleaned = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    if (cleaned !== codeInput.value) codeInput.value = cleaned;
  });
  form.addEventListener('input', () => {
    if (errorEl && !errorEl.hidden) clearError();
  });
  form.addEventListener('change', () => {
    if (errorEl && !errorEl.hidden) clearError();
  });
  backButton?.addEventListener('click', () => showStep('email'));

  // Client checks are UX only: the backend re-validates everything and is the authority.
  function validateCurrentStep() {
    if (step === 'email') {
      if (!EMAIL_PATTERN.test((emailInput.value || '').trim())) return ['Please enter a valid email address.', emailInput];
      return null;
    }
    if (!(nameInput.value || '').trim()) return ['Please enter your full name.', nameInput];
    if (!(instagramInput.value || '').replace(/^@+/, '').trim()) return ['Please enter your Instagram @ (for example @yourname).', instagramInput];
    if (!/^[A-Z0-9]{3,12}$/.test(codeInput.value || '')) return ['Choose a code of 3–12 letters or numbers.', codeInput];
    if (!consentInput.checked) return ['Please tick the box to continue.', consentInput];
    return null;
  }

  function turnstileToken() {
    return form.querySelector('[name="cf-turnstile-response"]')?.value || undefined;
  }

  function offerAlternatives(alternatives) {
    if (!suggestionsEl || !Array.isArray(alternatives) || !alternatives.length) return;
    suggestionsEl.textContent = 'Try: ';
    alternatives.slice(0, 3).forEach((alt) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = alt;
      button.addEventListener('click', () => {
        codeInput.value = String(alt).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
        clearError();
        codeInput.focus();
      });
      suggestionsEl.appendChild(button);
    });
    suggestionsEl.hidden = false;
  }

  async function post(payload) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(payload),
      credentials: 'omit',
      cache: 'no-store',
    });
    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }
    return { status: response.status, data };
  }

  form.addEventListener('submit', async (event) => {
    // This is OUR OWN endpoint, not Shopify's customer form: we handle the submit ourselves.
    event.preventDefault();
    if (busy) return;
    clearError();

    const invalid = validateCurrentStep();
    if (invalid) {
      showError(invalid[0], invalid[1]);
      return;
    }
    if (!endpoint) {
      showError('Something went wrong. Please try again in a moment.');
      return;
    }

    const payload = {
      step: step === 'email' ? 'check' : 'join',
      email: emailInput.value.trim(),
      lane: source,
      website: honeypot ? honeypot.value : '',
      turnstileToken: turnstileToken(),
    };
    if (step === 'details') {
      payload.fullName = nameInput.value.trim();
      payload.instagram = instagramInput.value.trim();
      payload.code = codeInput.value;
      payload.termsAccepted = consentInput.checked === true;
    }

    setBusy(true);
    publish('leopard_access_submitted', { source, step: payload.step });

    let result;
    try {
      result = await post(payload);
    } catch (networkError) {
      // No response was received: keep the SAME idempotency key so a retry can never double-create.
      setBusy(false);
      showError('We couldn’t reach Ancarta. Check your connection and try again — nothing has been created yet.');
      return;
    }
    // A definitive answer arrived: the next submit is a NEW attempt.
    idempotencyKey = newKey();
    window.turnstile?.reset?.();
    setBusy(false);

    const { status: code, data } = result;
    if (code === 200 && data && data.ok === true && data.access === true) {
      unlockUi(step === 'details' ? 'signup' : 'recognised');
      return;
    }
    if (code === 200 && data && data.ok === true && data.next === 'details') {
      showStep('details');
      return;
    }
    if (code === 409 && data && data.error === 'code_taken') {
      showError(data.message || 'That code has already been taken. Try another.', codeInput);
      offerAlternatives(data.alternatives);
      return;
    }
    if (code === 422 && data && Array.isArray(data.fieldErrors) && data.fieldErrors.length) {
      const first = data.fieldErrors[0];
      const map = { email: emailInput, fullName: nameInput, instagram: instagramInput, code: codeInput, termsAccepted: consentInput };
      showError(data.fieldErrors.map((e) => e.message).filter(Boolean).join(' ') || 'Please check your details.', map[first.field]);
      if (first.field === 'code') offerAlternatives(first.alternatives);
      if (first.field === 'email') showStep('email');
      return;
    }
    if (code === 429 || (data && data.error === 'rate_limited')) {
      showError('Too many attempts — please wait a few minutes and try again.');
      return;
    }
    if (code === 403 && data && data.error === 'verification_failed') {
      showError('Please complete the security check and try again.');
      return;
    }
    showError('Something went wrong. Please try again in a moment.');
  });

  if (submitButton) submitButton.disabled = false;
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && submitButton) submitButton.disabled = false;
  });
  if (form.dataset.turnstileKey) {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
  if (status) status.textContent = '';
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
